import { defineComponent as _defineComponent } from 'vue'
import { computed, ComputedRef, onBeforeMount, reactive, Ref, ref } from "vue";
import { useRoute } from "vue-router/composables";

import ProfileWrapper from "@/components/profile/ProfileWrapper.vue";
import ExportButton from "@/components/buttons/ExportButton.vue";
import ProfileList from "@/components/lists/ProfileList.vue";
import ProfilePanel from "@/components/profile/ProfilePanel.vue";
import StafferTable from "@/components/tables/StafferTable.vue";
import { ApiService, LogService } from "@/services";
import {
  VignetteCaucus,
  VignetteCommittee,
  VignetteGroupMember,
} from "@/types/nj/Groups";
import { MetadataKeys, VignetteHelpers, VignetteRoutes } from "@/types";
import { useViewAnalytics } from "@/composition/useViewAnalytics";
import NjBtn from "@/components/vuetify-wrappers/NjButton.vue";
import { useAppStore } from "@/pinia/app";

interface Props {
  type: "committee" | "caucus";
}

type Staffer = VignetteGroupMember[];


export default /*#__PURE__*/_defineComponent({
  __name: 'GroupDetail',
  props: {
    type: null
  },
  setup(__props: any) {

const props = __props as {
  type: "committee" | "caucus";
};

const appStore = useAppStore();


const isCaucus = computed(() => props.type === "caucus");
const isCommittee = computed(() => props.type === "committee");

const pageName = ref("");

const defaultOpenPanels = [
  "Contact Information",
  "Subcommittees",
  "Republican Members",
  "Democratic Members",
];

const route = useRoute();
const groupId = computed(() => +route.params.id);

const display404 = ref(false);

const group: Ref<VignetteCommittee | VignetteCaucus | null> = ref(null);

const briefcaseObj = reactive({
  id: 0,
  briefcaseId: 0,
  addedToBriefcase: false,
  longName: "",
  preferredNameDisplay: "",
  type: "Agency",
});

const presentationCenterLink = computed(() =>
  group.value?.presentationCenterUrl
    ? appStore.djangoSiteOrigin + group.value.presentationCenterUrl
    : ""
);

const groupIsCommittee = (
  g: VignetteCommittee | VignetteCaucus | null
): g is VignetteCommittee => {
  return g?.staffers !== undefined;
};
// const groupIsCaucus = (
//   g: VignetteCommittee | VignetteCaucus | null
// ): g is VignetteCaucus => {
//   return g?.staffers === undefined;
// };

const { setAnalyticsMetadata, pushPageViewEvent } = useViewAnalytics(pageName);

const setMetaData = (groupName: "caucus" | "committee", id: number) => {
  setAnalyticsMetadata(MetadataKeys.PAGE_TYPE, "Database");
  setAnalyticsMetadata(MetadataKeys.SITESECTION, "AlmanacPlus");
  setAnalyticsMetadata(MetadataKeys.CMS_CONTENT_ID, `${groupName}-${id}`);
  pushPageViewEvent();
};

// for brevity's sake
const allStaffers: ComputedRef<Staffer> = computed(() => {
  // type guard so that we can access the staffers property
  if (groupIsCommittee(group.value)) {
    const staffers = group.value.staffers as Staffer;
    return staffers.map((s) => ({
      ...s,
      positionSummary: s.positionSummary?.split(", Office of")?.[0] ?? "",
    }));
  } else {
    return [];
  }
});

const majorityStaffers = computed(() =>
  allStaffers.value.filter((s) => s.majmin == "MAJ")
);
const minorityStaffers = computed(() =>
  allStaffers.value.filter((s) => s.majmin == "MIN")
);
const otherStaffers = computed(() =>
  allStaffers.value.filter((s) => s.majmin == "OTH")
);

const chips: ComputedRef<{ display: string; to: string }[]> = computed(() => {
  const result = [];
  if (!group.value) return [];
  // both group types have a chamber field to parse through
  const chamber = group.value.chamber.toLowerCase();
  // display chamber(s), before the parent committee
  if (chamber == "joint") {
    result.push({ display: "Chamber: U.S. House of Representatives", to: "" });
    result.push({ display: "Chamber: U.S. Senate", to: "" });
  } else {
    const chamberDisplay =
      chamber == "house" ? "U.S. House of Representatives" : "U.S. Senate";
    result.push({ display: `Chamber: ${chamberDisplay}`, to: "" });
  }
  if (groupIsCommittee(group.value) && group.value.parent) {
    // if there is a parent committee, display it
    const display = "Committee: " + group.value.parent.shortName;
    const to = `/committee/${group.value.parent.id}`;
    result.push({ display, to });
  }
  return result;
});

const getCommittee = () => {
  ApiService.getCommittee(groupId.value)
    .then((response: VignetteCommittee) => {
      group.value = response;
      pageName.value = response.shortName;
      briefcaseObj.id = response.id;
      briefcaseObj.briefcaseId = response.briefcaseId;
      briefcaseObj.addedToBriefcase = response.addedToBriefcase;
      briefcaseObj.longName = response.longName;
      briefcaseObj.preferredNameDisplay = response.longName;
      briefcaseObj.type = response.type;
      setMetaData("committee", response.id);
    })
    .catch(() => {
      LogService.warn(
        `CommitteeDetail.vue: Error retrieving committee from groupId: ${groupId}`
      );
      display404.value = true;
    });
};

const getCaucus = () => {
  ApiService.getCaucus(groupId.value)
    .then((response: VignetteCaucus) => {
      group.value = response;
      pageName.value = response.shortName;
      briefcaseObj.id = response.id;
      briefcaseObj.briefcaseId = response.briefcaseId;
      briefcaseObj.addedToBriefcase = response.addedToBriefcase;
      briefcaseObj.longName = response.longName;
      briefcaseObj.preferredNameDisplay = response.longName;
      briefcaseObj.type = response.type;
      setMetaData("caucus", response.id);
    })
    .catch(() => {
      LogService.warn(
        `CommitteeDetail.vue: Error retrieving caucus from caucusId: ${groupId}`
      );
      display404.value = true;
    });
};

onBeforeMount(() => {
  if (isCommittee.value) {
    getCommittee();
  } else if (isCaucus.value) {
    getCaucus();
  }
});

return { __sfc: true,appStore, props, isCaucus, isCommittee, pageName, defaultOpenPanels, route, groupId, display404, group, briefcaseObj, presentationCenterLink, groupIsCommittee, setAnalyticsMetadata, pushPageViewEvent, setMetaData, allStaffers, majorityStaffers, minorityStaffers, otherStaffers, chips, getCommittee, getCaucus, ProfileWrapper, ExportButton, ProfileList, ProfilePanel, StafferTable, VignetteHelpers, VignetteRoutes, NjBtn }
}

})